import toast from "react-hot-toast";
import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { sendOtpForgotPin, verifyOtpForgotPin } from "../../../redux/ApiSlice/AccountPrivateSlice";

export default function EnterOtpContainer() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const [otpError, setOtpError] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const inputsRef = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

    const handleChange = (e, idx) => {
        const value = e.target.value.replace(/[^0-9]/g, "");
        if (!value) return;
        if (otpError) setOtpError("");
        const newOtp = [...otp];
        newOtp[idx] = value;
        setOtp(newOtp);
        if (idx < 5 && value) {
            inputsRef[idx + 1].current.focus();
        }
    };

    const handleKeyDown = (e, idx) => {
        if (e.key === "Backspace") {
            if (otp[idx]) {
                const newOtp = [...otp];
                newOtp[idx] = "";
                setOtp(newOtp);
            } else if (idx > 0) {
                const newOtp = [...otp];
                newOtp[idx - 1] = "";
                setOtp(newOtp);
                inputsRef[idx - 1].current.focus();
            }
        }
    };

    // Handle paste event for OTP fields
    const handlePaste = (e) => {
        const paste = e.clipboardData.getData("text").replace(/\D/g, "");
        if (paste.length === 0) return;
        const pasteArr = paste.split("").slice(0, 6);
        const newOtp = [...otp];
        pasteArr.forEach((num, idx) => {
            newOtp[idx] = num;
            if (inputsRef[idx].current) {
                inputsRef[idx].current.value = num;
            }
        });
        setOtp(newOtp);

        const lastIdx = pasteArr.length - 1;
        if (inputsRef[lastIdx] && inputsRef[lastIdx].current) {
            inputsRef[lastIdx].current.focus();
        }
        e.preventDefault();
    };

    const handleVerify = () => {
        if (otp.some(digit => digit === "")) {
            setOtpError("Please fill all 6 digits.");
            return;
        }

        setIsLoading(true);
        setOtpError("");

        dispatch(verifyOtpForgotPin({ otp: otp.join("") }))
            .then((result) => {
                const payload = result.payload;
                if (payload?.error || payload?.detail || payload?.message?.toLowerCase().includes("invalid")) {
                    const msg = payload?.error || payload?.detail || payload?.message || "Invalid OTP. Please try again.";
                    setOtpError(msg);
                    setOtp(["", "", "", "", "", ""]);
                    setTimeout(() => inputsRef[0].current?.focus(), 0);
                } else {
                    toast.success("OTP verified successfully!");
                    navigate(`/private/setPassword/${otp.join("")}`);
                }
            })
            .catch(() => {
                setOtpError("Something went wrong. Please try again.");
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <>
            {/* Header */}
            <div className="sticky top-0 z-10">
                <div className="relative bg-[#BDE6FF] rounded-b-xl h-12 flex items-center justify-center">
                    <IoMdArrowBack
                        onClick={() => navigate("/private")}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[22px] text-black dark:text-black cursor-pointer"
                    />
                    <p className="font-bold text-[18px] text-black dark:text-black line-clamp-1">
                        Verify Your Email
                    </p>
                </div>
            </div>

            {/* OTP Input Section */}
            <div className="h-full flex flex-col bg-white dark:bg-[#1E1D21]">
                <div className="flex flex-col items-center justify-center mt-8 px-4">
                    <p className="mb-8 text-center text-sm text-gray-600 dark:text-gray-400 max-w-sm">
                        We've sent a 6-digit verification code to your email. Please enter it below to continue.
                    </p>

                    {/* OTP Input Fields */}
                    <form className="w-full max-w-md" onSubmit={e => { e.preventDefault(); }}>
                        <div className="flex justify-center gap-3 mb-3">
                            {otp.map((digit, idx) => (
                                <input
                                    required
                                    key={idx}
                                    type="text"
                                    maxLength="1"
                                    value={digit}
                                    pattern="[0-9]"
                                    inputMode="numeric"
                                    ref={inputsRef[idx]}
                                    onPaste={handlePaste}
                                    autoComplete="one-time-code"
                                    onChange={e => handleChange(e, idx)}
                                    onKeyDown={e => handleKeyDown(e, idx)}
                                    className={`w-10 h-10 text-center text-xl font-bold rounded-md border-2 transition-all shadow-md focus:outline-none focus:ring-2 ${otpError
                                        ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-gray-800 dark:text-white focus:border-red-500 focus:ring-red-200 dark:focus:ring-red-800'
                                        : digit
                                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-400 text-gray-800 dark:text-white focus:border-blue-500 focus:ring-blue-200 dark:focus:ring-blue-800'
                                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:border-blue-500 focus:ring-blue-200 dark:focus:ring-blue-800'
                                        }`}
                                />
                            ))}
                        </div>

                        {/* Inline error message */}
                        <div className="min-h-[24px] mb-4 text-center">
                            {otpError && (
                                <p className="text-sm text-red-500 font-medium">{otpError}</p>
                            )}
                        </div>

                        {/* Verify Button */}
                        <div className="flex items-center justify-center mb-6">
                            <button
                                type="button"
                                onClick={handleVerify}
                                disabled={isLoading}
                                className="w-full max-w-xs h-12 rounded-xl bg-[#BDE6FF] hover:bg-[#A5D8F5] active:scale-95 flex items-center justify-center gap-2 text-black border border-black font-bold text-[16px] transition-all shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {isLoading ? (
                                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                    </svg>
                                ) : (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                )}
                                {isLoading ? "Verifying..." : "Verify OTP"}
                            </button>
                        </div>

                        {/* Resend OTP */}
                        <div className="text-center">
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                Didn't receive the code?
                            </p>
                            <button
                                type="button"
                                onClick={() => {
                                    dispatch(sendOtpForgotPin()).then((result) => {
                                        if (result.payload?.message) {
                                            toast.success(result.payload.message);
                                        } else {
                                            toast.error("Failed to resend OTP. Please try again.");
                                        }
                                    });
                                }}
                                className="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline transition-colors"
                            >
                                Resend OTP
                            </button>
                        </div>
                    </form>

                    {/* Help Text */}
                    <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 max-w-md">
                        <div className="flex gap-3">
                            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                                <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mb-1">
                                    Having trouble?
                                </p>
                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                    Check your spam folder or contact support if you don't receive the code within a few minutes.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}